import {MessageKey, t, tc} from '@/i18n'
import types from '@/model/types'
import {formulaTypeLabel, multipleLabel} from '@/utils/formulaCompletion'
import {FormulaProblem} from '@/types/FormulaVocabulary'

/**
 * A frase de um problema de fórmula, no idioma da tela — dona única: a recusa ao salvar e o editor
 * enquanto se digita dizem a MESMA coisa.
 *
 * Dois tipos de código chegam do servidor:
 *
 * - **os do app** (`formula_type`, `formula_cycle`…), com frase própria;
 * - **os do cel-js**, a biblioteca que lê e confere a fórmula, cuja mensagem vem em inglês.
 *
 * **Cada mensagem do cel-js é remontada por inteiro, com os seus parâmetros.** Elas são moldes
 * fixos no código da biblioteca (`Expected ${esperado}, got ${encontrado}`), e cada molde tem aqui
 * a expressão que recupera os valores e a chave da frase traduzida. Os valores também se traduzem:
 * os tipos do CEL viram os nomes do app (`double` → "Número", `list<Item_1>` → "Item (múltiplo)"),
 * os símbolos do parser viram o símbolo (`RPAREN` → ")"). Os moldes foram tirados do código-fonte
 * do @marcbachmann/cel-js 8.0.0 (lib/parser.js, operators.js, macros.js, optional.js,
 * functions.js, overloads.js, registry.js, type-checker.js).
 *
 * **O que não casa fica em inglês**: um código que não está aqui, ou uma mensagem cuja redação
 * mudou numa versão nova da biblioteca, mostra o texto que veio. Nunca uma frase errada — no pior
 * caso, a original. Os três códigos genéricos (`parse_error`, `type_error`, `evaluation_error`) são
 * os que a biblioteca usa quando ninguém deu um código, com mensagem livre; não há molde para eles.
 */

type Params = Record<string, string>
/** `count`, quando a frase tem plural ("só há 1 item" / "só há 3 itens"). */
type Template = { pattern: RegExp, key: MessageKey, params?: (match: RegExpExecArray) => Params, count?: (match: RegExpExecArray) => number }

/**
 * O tipo do CEL pelo nome do app; o próprio nome, quando o app não tem um (`map<…>`). A lista sem
 * o tipo do item — a biblioteca diz só `list` ao recusar um método — é "Múltiplo", como as outras.
 */
function type(name: string): string {
    return formulaTypeLabel(name === 'list' ? 'list<dyn>' : name) || name
}

function typeList(list: string): string {
    return list.split(', ').map(type).join(', ')
}

/** O nome da função de um `map(var, transform)`, de um `string.indexOf(...)`. */
function functionOf(description: string): string {
    const names = description.match(/[A-Za-z_]\w*(?=\()/g)
    return names ? names[names.length - 1] : description
}

/** Os símbolos do parser (lib/parser.js, `TOKEN`) como se escrevem na fórmula. */
const SYMBOLS: Record<string, string> = {
    PLUS: '+', MINUS: '-', MULTIPLY: '*', DIVIDE: '/', MODULO: '%', EQ: '==', NE: '!=', LT: '<', LE: '<=',
    GT: '>', GE: '>=', AND: '&&', OR: '||', NOT: '!', IN: 'in', LPAREN: '(', RPAREN: ')', LBRACKET: '[',
    RBRACKET: ']', LBRACE: '{', RBRACE: '}', DOT: '.', COMMA: ',', COLON: ':', QUESTION: '?', NULL: 'null'
}
/** Os que não são um símbolo, e sim uma espécie de coisa: "um nome", "o fim da fórmula". */
const TOKEN_WORDS: Record<string, MessageKey> = {
    EOF: 'formula-cel.token-end', IDENTIFIER: 'formula-cel.token-name', NUMBER: 'formula-cel.token-number',
    STRING: 'formula-cel.token-text', BOOLEAN: 'formula-cel.token-boolean', BYTES: 'formula-cel.token-bytes'
}

function token(name: string): string {
    if (SYMBOLS[name]) return `"${SYMBOLS[name]}"`
    return TOKEN_WORDS[name] ? t(TOKEN_WORDS[name]) : name
}

/** Os limites do cel-js (lib/options.js, `DEFAULT_LIMITS`) e o que cada um conta. */
const LIMITS: Record<string, MessageKey> = {
    maxAstNodes: 'formula-cel.limit-nodes', maxDepth: 'formula-cel.limit-depth',
    maxListElements: 'formula-cel.limit-list', maxMapEntries: 'formula-cel.limit-map',
    maxCallArguments: 'formula-cel.limit-arguments'
}

/** Os moldes de cada código, na ordem em que se tenta casar. */
const CEL: Record<string, Template[]> = {
    //— Leitura do texto (lib/parser.js) —
    expression_must_be_string: [{pattern: /^Expression must be a string$/, key: 'formula-cel.expression-must-be-string'}],
    unexpected_character: [{pattern: /^Unexpected character: '?(.+?)'?$/, key: 'formula-cel.unexpected-character',
        params: m => ({character: m[1]})}],
    unexpected_token: [
        {pattern: /^Unexpected token: EOF$/, key: 'formula-cel.unexpected-end'},
        {pattern: /^Unexpected token: (\w+)$/, key: 'formula-cel.unexpected-token', params: m => ({token: token(m[1])})}
    ],
    expected_token: [{pattern: /^Expected (\w+), got (\w+)$/, key: 'formula-cel.expected-token',
        params: m => ({expected: token(m[1]), found: token(m[2])})}],
    invalid_number: [{pattern: /^Invalid number: (.*)$/, key: 'formula-cel.invalid-number', params: m => ({value: m[1]})}],
    invalid_integer: [{pattern: /^Invalid integer: (.*)$/, key: 'formula-cel.invalid-integer', params: m => ({value: m[1]})}],
    invalid_hex_integer: [{pattern: /^Invalid hex integer: (.*)$/, key: 'formula-cel.invalid-hex-integer',
        params: m => ({value: m[1]})}],
    invalid_exponent: [{pattern: /^Invalid exponent$/, key: 'formula-cel.invalid-exponent'}],
    newline_in_string: [{pattern: /^Newlines not allowed in single-quoted strings$/, key: 'formula-cel.newline-in-string'}],
    unterminated_string: [{pattern: /^Unterminated string$/, key: 'formula-cel.unterminated-string'}],
    unterminated_triple_quoted_string: [{pattern: /^Unterminated triple-quoted string$/,
        key: 'formula-cel.unterminated-triple-quoted-string'}],
    //A barra invertida vai no parâmetro, e não na frase: no texto do i18n ela é caractere especial
    bytes_unicode_escape: [{pattern: /^\\(.+) not allowed in bytes literals$/, key: 'formula-cel.bytes-unicode-escape',
        params: m => ({escape: `\\${m[1]}`})}],
    invalid_unicode_escape: [{pattern: /^Invalid Unicode escape: \\(.+)$/, key: 'formula-cel.invalid-unicode-escape',
        params: m => ({escape: `\\${m[1]}`})}],
    invalid_unicode_surrogate: [{pattern: /^Invalid Unicode surrogate: \\(.+)$/, key: 'formula-cel.invalid-unicode-surrogate',
        params: m => ({escape: `\\${m[1]}`})}],
    invalid_hex_escape: [{pattern: /^Invalid hex escape: \\(.+)$/, key: 'formula-cel.invalid-hex-escape',
        params: m => ({escape: `\\${m[1]}`})}],
    invalid_octal_escape: [{pattern: /^Octal escape must be 3 digits$/, key: 'formula-cel.invalid-octal-escape'}],
    octal_escape_out_of_range: [{pattern: /^Octal escape out of range: \\(.+)$/, key: 'formula-cel.octal-escape-out-of-range',
        params: m => ({escape: `\\${m[1]}`})}],
    invalid_escape_sequence: [{pattern: /^Invalid escape sequence: \\(.+)$/, key: 'formula-cel.invalid-escape-sequence',
        params: m => ({escape: `\\${m[1]}`})}],
    reserved_identifier: [{pattern: /^Reserved identifier: (.+)$/, key: 'formula-cel.reserved-identifier',
        params: m => ({name: m[1]})}],
    limit_exceeded: Object.entries(LIMITS).map(([limit, key]) => ({
        pattern: new RegExp(`^Exceeded ${limit} \\((\\d+)\\)$`), key, params: (m: RegExpExecArray) => ({limit: m[1]})
    })),
    //— As macros (lib/macros.js): o prefixo é a assinatura da macro, `map(var, transform)` —
    invalid_macro_argument: [
        {pattern: /^(.+\)) invalid predicate iteration variable$/, key: 'formula-cel.invalid-iteration-variable',
            params: m => ({function: functionOf(m[1])})},
        {pattern: /^has\(\) invalid argument$/, key: 'formula-cel.invalid-has-argument'}
    ],
    //— A checagem de nomes e tipos (lib/operators.js, type-checker.js, registry.js) —
    unknown_variable: [{pattern: /^Unknown variable: (.+)$/, key: 'formula-cel.unknown-variable', params: m => ({name: m[1]})}],
    no_such_key: [{pattern: /^No such key: (.+)$/, key: 'formula-cel.no-such-key', params: m => ({name: m[1]})}],
    no_such_overload: [
        {pattern: /^no such overload: (\S+) (\S+) (\S+)$/, key: 'formula-cel.no-such-binary',
            params: m => ({left: type(m[1]), operator: m[2], right: type(m[3])})},
        {pattern: /^no such overload: ([-!])(\S+)$/, key: 'formula-cel.no-such-unary',
            params: m => ({operator: m[1], type: type(m[2])})}
    ],
    no_matching_overload: [
        {pattern: /^found no matching overload for '(.+)\.(\w+)\(\)'$/, key: 'formula-cel.no-matching-method',
            params: m => ({function: m[2], receiver: type(m[1])})},
        {pattern: /^found no matching overload for '(.+)\.(\w+)\((.+)\)'$/, key: 'formula-cel.no-matching-method-arguments',
            params: m => ({function: m[2], receiver: type(m[1]), arguments: typeList(m[3])})},
        {pattern: /^found no matching overload for '(\w+)\(\)'$/, key: 'formula-cel.no-matching-function',
            params: m => ({function: m[1]})},
        {pattern: /^found no matching overload for '(\w+)\((.+)\)'$/, key: 'formula-cel.no-matching-function-arguments',
            params: m => ({function: m[1], arguments: typeList(m[2])})}
    ],
    invalid_condition_type: [
        {pattern: /^Ternary condition must be bool, got '(.+)'$/, key: 'formula-cel.ternary-condition',
            params: m => ({type: type(m[1])})},
        //O rótulo que a macro põe no predicado: `filter(var, predicate) predicate must return bool`
        {pattern: /^(.+\)) (?:filter )?predicate must return bool, got '(.+)'$/, key: 'formula-cel.predicate-condition',
            params: m => ({function: functionOf(m[1]), type: type(m[2])})}
    ],
    //Os operadores como parâmetro, e não escritos na frase: `|` separa plurais no vue-i18n
    invalid_logical_operand: [{pattern: /^Logical operator requires bool operands, got '(.+)'$/,
        key: 'formula-cel.invalid-logical-operand', params: m => ({type: type(m[1]), and: '&&', or: '||'})}],
    incompatible_ternary_branches: [{pattern: /^Ternary branches must have the same type, got '(.+)' and '(.+)'$/,
        key: 'formula-cel.incompatible-ternary-branches', params: m => ({left: type(m[1]), right: type(m[2])})}],
    //O mesmo código para `nome.campo` e `nome[0]` num tipo que não tem nem um nem outro
    cannot_index_type: [{pattern: /^Cannot index type '(.+)'$/, key: 'formula-cel.cannot-index-type',
        params: m => ({type: type(m[1])})}],
    invalid_index_type: [
        {pattern: /^List index must be int, got '(.+)'$/, key: 'formula-cel.list-index-type', params: m => ({type: type(m[1])})},
        {pattern: /^Cannot index type '(.+)' with type '(.+)'$/, key: 'formula-cel.invalid-index-type',
            params: m => ({type: type(m[1]), index: type(m[2])})}
    ],
    invalid_comprehension_range: [{pattern: /^Expression of type '(.+)' cannot be range of a comprehension/,
        key: 'formula-cel.invalid-comprehension-range', params: m => ({type: type(m[1])})}],
    heterogeneous_list_element: [{pattern: /^List elements must have the same type, expected type '(.+)' but found '(.+)'$/,
        key: 'formula-cel.heterogeneous-list-element', params: m => ({expected: type(m[1]), found: type(m[2])})}],
    heterogeneous_map_key: [{pattern: /^Map key uses wrong type, expected type '(.+)' but found '(.+)'$/,
        key: 'formula-cel.heterogeneous-map-key', params: m => ({expected: type(m[1]), found: type(m[2])})}],
    heterogeneous_map_value: [{pattern: /^Map value uses wrong type, expected type '(.+)' but found '(.+)'$/,
        key: 'formula-cel.heterogeneous-map-value', params: m => ({expected: type(m[1]), found: type(m[2])})}],
    unsupported_type: [{pattern: /^Unsupported type: (.+)$/, key: 'formula-cel.unsupported-type', params: m => ({type: m[1]})}],
    field_type_mismatch: [{pattern: /^Field '(.+)' is not of type '(.+)', got '(.+)'$/, key: 'formula-cel.field-type-mismatch',
        params: m => ({name: m[1], expected: type(m[2]), found: type(m[3])})}],
    list_item_type_mismatch: [{pattern: /^List item with index '(.+)' is not of type '(.+)', got '(.+)'$/,
        key: 'formula-cel.list-item-type-mismatch', params: m => ({index: m[1], expected: type(m[2]), found: type(m[3])})}],
    variable_type_mismatch: [{pattern: /^Variable '(.+)' is not of type '(.+)', got '(.+)'$/,
        key: 'formula-cel.variable-type-mismatch', params: m => ({name: m[1], expected: type(m[2]), found: type(m[3])})}],
    //— O valor opcional (lib/optional.js) —
    optional_expected: [{pattern: /^(.+\)) receiver must be optional(?:, got '(.+)')?$/, key: 'formula-cel.optional-expected',
        params: m => ({function: functionOf(m[1])})}],
    incompatible_argument_type: [{pattern: /^(.+\)) argument must be compatible type, got '(.+)' and '(.+)'$/,
        key: 'formula-cel.incompatible-argument-type',
        params: m => ({function: functionOf(m[1]), left: type(m[2]), right: type(m[3])})}],
    invalid_optional_argument: [{pattern: /^Optional\.or must be called with an Optional argument$/,
        key: 'formula-cel.invalid-optional-argument'}],
    optional_value_missing: [{pattern: /^Optional value is not present$/, key: 'formula-cel.optional-value-missing'}],
    //— A conta, na execução (lib/overloads.js, functions.js, registry.js) —
    division_by_zero: [{pattern: /^division by zero$/, key: 'formula-cel.division-by-zero'}],
    modulo_by_zero: [{pattern: /^modulo by zero$/, key: 'formula-cel.modulo-by-zero'}],
    numeric_overflow: [
        {pattern: /^integer overflow: (.+)$/, key: 'formula-cel.integer-overflow', params: m => ({value: m[1]})},
        {pattern: /^(?:Unsigned integer overflow|u?int\(\) type error: (?:unsigned )?integer overflow)$/,
            key: 'formula-cel.numeric-overflow'}
    ],
    int_conversion_error: [{pattern: /^int\(\) type error: cannot convert to int$/, key: 'formula-cel.int-conversion'}],
    uint_conversion_error: [{pattern: /^uint\(\) type error: cannot convert to uint$/, key: 'formula-cel.uint-conversion'}],
    double_conversion_error: [{pattern: /^double\(\) type error: cannot convert to double$/, key: 'formula-cel.double-conversion'}],
    bool_conversion_error: [{pattern: /^bool\(\) conversion error: invalid string value "(.*)"$/, key: 'formula-cel.bool-conversion',
        params: m => ({value: m[1]})}],
    index_out_of_range: [{pattern: /^string\.(\w+)\(.*\): \w+ (?:index )?out of range$/, key: 'formula-cel.index-out-of-range',
        params: m => ({function: m[1]})}],
    index_out_of_bounds: [
        {pattern: /^No such key: index out of bounds, index (-?\d+) < 0$/, key: 'formula-cel.index-negative',
            params: m => ({index: m[1]})},
        {pattern: /^No such key: index out of bounds, index (\d+) >= size (\d+)$/, key: 'formula-cel.index-beyond-size',
            params: m => ({index: m[1], size: m[2]}), count: m => Number(m[2])}
    ],
    invalid_timestamp: [
        {pattern: /^timestamp\(\) requires a string in ISO 8601 format$/, key: 'formula-cel.timestamp-text'},
        {pattern: /^timestamp\(\) requires a valid integer unix timestamp$/, key: 'formula-cel.timestamp-number'}
    ],
    invalid_duration: [{pattern: /^Invalid duration string: (.*)$/, key: 'formula-cel.invalid-duration',
        params: m => ({value: m[1]})}],
    invalid_regular_expression: [{pattern: /^Invalid regular expression: (.*)$/, key: 'formula-cel.invalid-regular-expression',
        params: m => ({value: m[1]})}],
    invalid_comparison_type: [{pattern: /^Cannot compare values of type (.+)$/, key: 'formula-cel.invalid-comparison-type',
        params: m => ({type: m[1]})}],
    invalid_list_element_type: [{pattern: /^string\.join\((?:separator)?\): list must contain only strings$/,
        key: 'formula-cel.invalid-list-element-type'}],
    invalid_context: [{pattern: /^Context must be an object$/, key: 'formula-cel.invalid-context'}]
}

/**
 * Os nomes de tipo que as frases citam sem receber de parâmetro ("precisa ser Sim/Não") — os do
 * app, os mesmos do seletor "Tipo".
 */
function appTypes(): Params {
    return {boolean: types.boolean.name, number: types.number.name, text: types.text.name}
}

/** A mensagem do cel-js remontada na língua da tela, ou `undefined` quando nenhum molde casa. */
export function celMessage(code: string, message: string): string | undefined {
    for (const template of CEL[code] || []) {
        const match = template.pattern.exec(message)
        if (!match) continue
        const values = {...appTypes(), ...(template.params ? template.params(match) : {})}
        return template.count ? tc(template.key, template.count(match), values) : t(template.key, values)
    }
    return undefined
}

/**
 * Os códigos de problema de fórmula que o próprio app define (ver lunedi/src/formula). A chave
 * inteira, e não só o sufixo: o `t` confere a chave pelo tipo, e uma montada por template não
 * passa nessa conferência.
 */
const APP_ERRORS = {
    formula_type: 'formula-error.type',
    formula_cycle: 'formula-error.cycle',
    forbidden_function: 'formula-error.forbidden',
    nested_comprehension: 'formula-error.nested',
    formula_on_relationship: 'formula-error.unsupported',
    formula_on_media: 'formula-error.unsupported',
    //O alias comparado com uma seleção ou situação que não é nenhuma delas — ver lunedi/src/formula/citations.ts
    unknown_option: 'formula-error.unknown-option',
    unknown_state: 'formula-error.unknown-state',
    //O alias de papel que o ambiente não tem (`"dev" in currentUser.roles`) — também citations.ts
    unknown_role: 'formula-error.unknown-role',
    //A calculada de usuário que dá uma conta que não é pessoa do ambiente — ver lunedi/src/formula/values.ts
    unknown_user: 'formula-error.unknown-user',
    //O acesso: a ordem fixa, a roda entre coleções e o encadeamento longo — ver lunedi/src/formula/check.ts
    access_order: 'formula-error.access-order',
    access_cycle: 'formula-error.access-cycle',
    access_too_deep: 'formula-error.access-too-deep',
    //Relacionamento com espelho não se calcula — ver lunedi/src/formula/check.ts
    formula_mirrored: 'formula-error.mirrored',
    //Regra em calculada ou mídia — ver lunedi/src/formula/check.ts
    rule_unsupported: 'formula-error.rule-unsupported',
    //"Opções permitidas" fora de relacionamento e seleção, ou em calculada — idem
    option_filter_unsupported: 'formula-error.option-filter-unsupported',
    //O valor padrão: num campo que não o tem, e os padrões que se leem em roda — idem
    default_unsupported: 'formula-error.default-unsupported',
    default_cycle: 'formula-error.default-cycle'
} as const

/**
 * A frase de um problema de fórmula. Nenhuma cita o nome do atributo: na criação ele ainda não
 * existe, e a frase saía "A fórmula de dá…".
 *
 * A frase traduzida vai sozinha, sem prefixo: ela já diz o que houve (pedido do usuário). O
 * "Erro na fórmula:" fica só na frente do texto ORIGINAL, o do código sem molde — ali a mensagem
 * está em inglês, e o prefixo é a parte no idioma da tela que diz do que se trata.
 *
 * Só a primeira linha da mensagem original: as seguintes são o desenho do trecho, com a seta
 * embaixo, que sem fonte monoespaçada só confunde (e no editor o trecho já vem sublinhado).
 */
export function formulaProblemMessage(problem: Pick<FormulaProblem, 'code' | 'message'>): string {
    const original = String(problem.message || '').split('\n')[0]
    //O tipo pelos nomes do app, e não os do CEL: o servidor manda "bool → choice[]" — o tipo que
    //saiu, e o tipo do atributo, com `[]` no múltiplo (ver check.ts)
    const mismatch = problem.code === 'formula_type' && /^(\S+) → (\w+)(\[\])?$/.exec(original)
    if (mismatch) {
        //Tipo do app (`text`, `choice`) ou, num relacionamento, a coleção de destino (`Cidade_1`)
        const attribute = types[mismatch[2]] ? types[mismatch[2]].name : type(mismatch[2])
        const expected = multipleLabel(attribute, !!mismatch[3])
        return t('formula-error.type', {result: type(mismatch[1]), expected})
    }
    //Na regra, quem tem de dar Sim/Não é a FÓRMULA, seja qual for o tipo do atributo: a frase do
    //calculado ("mas o atributo é Sim/Não") falava de um atributo que podia ser número
    if (problem.code === 'rule_type')
        return t('formula-error.rule-type', {result: type(original), expected: types.boolean.name})
    const own = APP_ERRORS[problem.code as keyof typeof APP_ERRORS]
    if (own) return t(own, {detail: original})
    return celMessage(problem.code, original) || t('formula-error.invalid', {detail: original})
}
